import { useEffect, useRef } from 'react';

function stableRowId(raw, chartRow) {
  if (!raw) return null;
  if (raw.sample_id != null && String(raw.sample_id).trim() !== '') {
    return String(raw.sample_id);
  }
  if (raw.sample_number != null && String(raw.sample_number).trim() !== '') {
    return String(raw.sample_number);
  }
  if (raw.orig_sample_number != null || raw.dup_sample_number != null) {
    return `${raw.orig_sample_number ?? ''}|${raw.dup_sample_number ?? ''}`;
  }
  if (chartRow?.index != null) return `idx:${chartRow.index}`;
  return null;
}

function sampleCandidates(chartRow) {
  if (!chartRow) return [];
  return [
    chartRow.sampleNumber,
    chartRow.origSampleNumber,
    chartRow.dupSampleNumber,
    chartRow.raw?.sample_number,
    chartRow.raw?.orig_sample_number,
    chartRow.raw?.dup_sample_number,
    chartRow.raw?.sample_id,
  ].filter((v) => v != null && String(v).trim() !== '');
}

function rowMatchesFilterExactly(chartRow, filterLower) {
  return sampleCandidates(chartRow).some(
    (c) => String(c).trim().toLowerCase() === filterLower
  );
}

/**
 * When the sample (or workorder) filter narrows to exactly one chart point,
 * or exactly one exact sample-number match, call onPointClick(row.raw)
 * so details/surrounding samples load without a graph click.
 */
export function useAutoSelectSingleFilterMatch(sampleFilter, filteredChartData, onPointClick) {
  const lastKeyRef = useRef('');

  useEffect(() => {
    const q = (sampleFilter || '').trim();
    if (!q) {
      lastKeyRef.current = '';
      return;
    }
    if (!onPointClick || !filteredChartData || filteredChartData.length === 0) return;

    let row = null;
    if (filteredChartData.length === 1) {
      row = filteredChartData[0];
    } else {
      const qLower = q.toLowerCase();
      const exact = filteredChartData.filter((d) => rowMatchesFilterExactly(d, qLower));
      if (exact.length === 1) {
        row = exact[0];
      } else {
        return;
      }
    }

    const raw = row.raw;
    if (!raw) return;

    const id = stableRowId(raw, row);
    if (id == null) return;

    const key = `${q}|${id}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    onPointClick(raw);
  }, [sampleFilter, filteredChartData, onPointClick]);
}
